// Beitragsschnittstellen je Plattform (Besitzer: geruest). Alle Hooks filtern nach `visible(viewer)`
// und sortieren nach `order`. Hosts: people (Profil-Tabs), home (Widgets), Shells von ui-basis (Rest).
import { useMemo } from 'react';
import { useRegistryContext } from './context';
import type {
  AccountMenuItem,
  AdminContribution,
  Command,
  CommandProvider,
  DockPanel,
  PeopleContribution,
  ProfileTab,
  TopbarItem,
  Viewer,
  Widget,
} from './types';
import { visibleSorted } from './visibility';

type AnyContribution = PeopleContribution & AdminContribution;

function useCollected<T extends { order?: number; visible?: (viewer: Viewer) => boolean }>(
  pick: (c: AnyContribution) => readonly T[] | undefined,
): T[] {
  const { registry, viewer } = useRegistryContext();
  // `pick` ist immer eine Funktion auf Modulebene, also stabil.
  return useMemo(
    () =>
      visibleSorted(
        registry.modules.flatMap((m) => pick(m.contribution as AnyContribution) ?? []),
        viewer,
      ),
    [registry, viewer, pick],
  );
}

const pickProfileTabs = (c: AnyContribution) => c.profileTabs;
const pickHomeWidgets = (c: AnyContribution) => c.homeWidgets;
const pickAdminWidgets = (c: AnyContribution) => c.adminOverviewWidgets;
const pickCommands = (c: AnyContribution) => c.commands;
const pickProviders = (c: AnyContribution) => c.commandProviders;
const pickTopbar = (c: AnyContribution) => c.topbarItems;
const pickAccountMenu = (c: AnyContribution) => c.accountMenuItems;

/** People: Tabs der Profilseite /people/:id (Host: Modul people). */
export function useProfileTabs(personId?: string): ProfileTab[] {
  const { viewer } = useRegistryContext();
  const tabs = useCollected(pickProfileTabs);
  return personId ? tabs.filter((tab) => tab.visibleFor?.(viewer, personId) ?? true) : tabs;
}

/** People: Widgets der Startseite (Host: Modul home). */
export const useHomeWidgets = (): Widget[] => useCollected(pickHomeWidgets);

/** Admin: Widgets der Übersicht /admin/ (Host: Modul admin). */
export const useAdminOverviewWidgets = (): Widget[] => useCollected(pickAdminWidgets);

/** Alle Plattformen: statische Befehle der Befehlspalette. */
export const useCommands = (): Command[] => useCollected(pickCommands);

/** Alle Plattformen: Suchanbieter der Befehlspalette. */
export const useCommandProviders = (): CommandProvider[] => useCollected(pickProviders);

/** People/Admin/AI: Einträge rechts in der Topbar (z. B. Chat-Icon mit Badge). */
export const useTopbarItems = (): TopbarItem[] => useCollected(pickTopbar);

/** Alle Plattformen: Einträge im Avatar-Menü. */
export const useAccountMenuItems = (): AccountMenuItem[] => useCollected(pickAccountMenu);

/** People: Dock-Panels (Chat). Nicht nach Rolle gefiltert; das Panel prüft selbst. */
export function useDockPanels(): DockPanel[] {
  const { registry } = useRegistryContext();
  return useMemo(
    () => registry.modules.flatMap((m) => (m.contribution as PeopleContribution).dockPanels ?? []),
    [registry],
  );
}
